import { useCallback, useEffect, useMemo, useState } from 'react';
import { get, set } from 'idb-keyval';
import { Kontraktsomrade } from '@/domain/omrader';
import type { Riksvegrute as ApiRiksvegrute } from '@/generated/nvdb/vegobjekter/models';
import type { Vegobjekttype as ApiVegobjekttype } from '@/generated/nvdb/datakatalog/models';
import { Data, Datakatalog } from './data';
import {
    FylkeApiResponse,
    KontraktsomradeApiResponse,
    transformFylke,
    transformKommune,
    transformKontraktsomrade,
    transformRiksvegrute,
    transformVegobjekttype,
} from '@/server/transformers';
import type { KommuneApiResponse } from '@/server/transformers';
import { getClientSessionId, getHeaders } from '@/server/server';
import { getApiURLArea, getDaKatApiURL } from '@/utils/Utils';
import { useSelector } from '@/state/store';
import { stateConfig } from '@/selectors/selectors';

interface StoredData<T = unknown> {
    etag: string | undefined | null;
    data: T | undefined;
}

interface IDBRevalidateResult<T> {
    data: T | undefined;
    error: Error | undefined;
    serviceUnavailable: boolean;
}

interface DataLoaderResult {
    data: Data | null;
    datakatalogServiceUnavailable: boolean;
}

interface RevalidateResult<T> {
    data: StoredData<T>;
    serviceUnavailable: boolean;
}

const isDefined = <T>(value: T | null | undefined): value is T => value != null;
const storedDataCache = new Map<string, StoredData<unknown>>();
const serviceUnavailableCache = new Map<string, boolean>();
const idbLoadCache = new Map<string, Promise<StoredData<unknown>>>();
const revalidateCache = new Map<string, Promise<RevalidateResult<unknown>>>();
const sessionValidatedUrls = new Set<string>();

const getCachedStoredData = <T>(url: string): StoredData<T> | undefined => {
    return storedDataCache.get(url) as StoredData<T> | undefined;
};

const setCachedStoredData = <T>(url: string, data: StoredData<T>) => {
    storedDataCache.set(url, data as StoredData<unknown>);
};

const loadStoredDataFromIDB = <T>(url: string): Promise<StoredData<T>> => {
    const cached = getCachedStoredData<T>(url);
    if (cached) return Promise.resolve(cached);

    const inFlight = idbLoadCache.get(url) as Promise<StoredData<T>> | undefined;
    if (inFlight) return inFlight;

    const promise = get(url)
        .then(loaded => {
            let storedData: StoredData<T>;
            if (loaded) {
                if (typeof loaded !== 'object') {
                    throw Error(
                        `Data from IDB ${loaded} is not a valid StoredData Entry`
                    );
                }
                storedData = loaded as StoredData<T>;
            } else {
                storedData = { data: undefined, etag: null };
            }

            setCachedStoredData(url, storedData);
            return storedData;
        })
        .finally(() => {
            idbLoadCache.delete(url);
        });

    idbLoadCache.set(url, promise as Promise<StoredData<unknown>>);
    return promise;
};

const revalidateStoredData = <T>(
    url: string,
    currentData: StoredData<T>
): Promise<RevalidateResult<T>> => {
    const inFlight = revalidateCache.get(url) as Promise<RevalidateResult<T>> | undefined;
    if (inFlight) return inFlight;

    const headers = getHeaders(getClientSessionId());
    const request = currentData.etag
        ? fetch(url, { headers: { 'If-None-Match': currentData.etag, ...headers } })
        : fetch(url, { headers });

    const promise = request
        .then(async response => {
            if (response.status === 302) {
                sessionValidatedUrls.add(url);
                serviceUnavailableCache.set(url, false);
                return { data: currentData, serviceUnavailable: false };
            }

            if (response.status === 200) {
                const updated = {
                    etag: response.headers.get('etag'),
                    data: (await response.json()) as T,
                };
                setCachedStoredData(url, updated);
                serviceUnavailableCache.set(url, false);
                sessionValidatedUrls.add(url);
                await set(url, updated);
                return { data: updated, serviceUnavailable: false };
            }

            if (response.status === 304) {
                setCachedStoredData(url, currentData);
                serviceUnavailableCache.set(url, false);
                sessionValidatedUrls.add(url);
                return { data: currentData, serviceUnavailable: false };
            }

            if (response.status === 503 && currentData.data === undefined) {
                throw new DataLoaderServiceUnavailableError();
            }

            if (response.status === 503) {
                serviceUnavailableCache.set(url, true);
                sessionValidatedUrls.add(url);
                return { data: currentData, serviceUnavailable: true };
            }

            if (response.status >= 400) {
                throw new Error(
                    `Request for ${url} failed with status ${response.status}`
                );
            }

            serviceUnavailableCache.set(url, false);
            sessionValidatedUrls.add(url);
            return { data: currentData, serviceUnavailable: false };
        })
        .finally(() => {
            revalidateCache.delete(url);
        });

    revalidateCache.set(url, promise as Promise<RevalidateResult<unknown>>);
    return promise;
};

export const resetDataLoaderCacheForTests = () => {
    storedDataCache.clear();
    serviceUnavailableCache.clear();
    idbLoadCache.clear();
    revalidateCache.clear();
    sessionValidatedUrls.clear();
};

export class DataLoaderServiceUnavailableError extends Error {
    constructor() {
        super(
            'Vegkart kunne ikke laste inn data fordi tjenesten er midlertidig utilgjengelig.'
        );
        this.name = 'DataLoaderServiceUnavailableError';
    }
}

/**
 * Fetch data, cache in IndexedDB and revalidated using ETags
 * Inspired by the 'stale-while-revalidate' cache strategy.
 * If data is available in indexeddb, this will be used while ETags are validated.
 */
const useIDBRevalidate = <T>(url: string): IDBRevalidateResult<T> => {
    const [error, setError] = useState<Error>();
    const [serviceUnavailable, setServiceUnavailable] = useState(
        () => serviceUnavailableCache.get(url) ?? false
    );
    const [data, setData] = useState<StoredData<T>>(
        () => getCachedStoredData<T>(url) ?? { etag: undefined, data: undefined }
    );

    useEffect(() => {
        setData(getCachedStoredData<T>(url) ?? { etag: undefined, data: undefined });
        setServiceUnavailable(serviceUnavailableCache.get(url) ?? false);
    }, [url]);

    const loadFromIDB = useCallback(() => {
        loadStoredDataFromIDB<T>(url)
            .then(loaded => {
                setData(loaded);
            })
            .catch(e => {
                setError(e);
            });
    }, [url]);

    const loadFromRemote = useCallback(() => {
        revalidateStoredData(url, data)
            .then(result => {
                setData(result.data);
                setServiceUnavailable(result.serviceUnavailable);
            })
            .catch(e => {
                setError(e instanceof Error ? e : new Error(String(e)));
            });
    }, [data, url]);

    useEffect(() => {
        if (error) throw error;
    }, [error]);
    useEffect(() => {
        if (data.etag === undefined) loadFromIDB();
        else if (!sessionValidatedUrls.has(url)) loadFromRemote();
    }, [url, data.etag, loadFromIDB, loadFromRemote]);
    return { data: data.data, error, serviceUnavailable };
};
export const useDataLoader = (): DataLoaderResult => {
    const config = useSelector(stateConfig);
    const baseUrl = getApiURLArea(config);
    const { data: counties } = useIDBRevalidate<FylkeApiResponse[]>(
        `${baseUrl}/fylker?inkluder=kartutsnitt,senterpunkt,vegobjekt`
    );
    const { data: municipalities } = useIDBRevalidate<KommuneApiResponse[]>(
        `${baseUrl}/kommuner?inkluder=kartutsnitt,senterpunkt,vegobjekt`
    );
    const { data: riksvegruter } = useIDBRevalidate<ApiRiksvegrute[]>(
        `${baseUrl}/riksvegruter?inkluder=vegobjekt`
    );
    const { data: contractAreas } = useIDBRevalidate<KontraktsomradeApiResponse[]>(
        `${baseUrl}/kontraktsomrader?inkluder=vegobjekt`
    );
    const { data: featureTypes, serviceUnavailable: datakatalogServiceUnavailable } =
        useIDBRevalidate<ApiVegobjekttype[]>(getDaKatApiURL(config));
    const datacatalog = useMemo(
        () =>
            featureTypes &&
            new Datakatalog(featureTypes?.map(f => transformVegobjekttype(f))),
        [featureTypes]
    );
    const ready =
        municipalities && counties && contractAreas && datacatalog && riksvegruter;
    const data = useMemo(
        () =>
            ready
                ? new Data(
                      datacatalog,
                      counties.map(fylke => transformFylke(fylke)).filter(isDefined),
                      municipalities
                          .map(kommune => transformKommune(kommune))
                          .filter(isDefined),
                      contractAreas.map(transformKontraktsomrade).filter(isDefined),
                      riksvegruter.map(transformRiksvegrute).filter(isDefined)
                  )
                : null,
        [ready, datacatalog, counties, municipalities, contractAreas, riksvegruter]
    );
    return {
        data: ready ? data : null,
        datakatalogServiceUnavailable: ready ? datakatalogServiceUnavailable : false,
    };
};
